import React, { useState, useEffect } from 'react';
import { 
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ChatAlt2Icon,
  BellIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationIcon,
  UserGroupIcon,
  DocumentTextIcon,
  PlusIcon,
  StarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  SearchIcon,
  FilterIcon,
  RefreshIcon,
  CalendarIcon,
  LocationMarkerIcon,
  LightningBoltIcon,
  SparklesIcon,
  ChartBarIcon,
  CogIcon,
  TagIcon,
  ClipboardListIcon,
  TrendingUpIcon,
  GlobeAltIcon,
  PhoneIcon,
  MailIcon,
  LockClosedIcon
} from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BuyerDashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Mock data - replace with real Firebase data
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalRFQs: 15,
      activeRFQs: 8,
      completedRFQs: 7,
      totalSpent: 3500000,
      avgResponseTime: 4.2,
      avgQuotesPerRFQ: 12,
      thisMonthRFQs: 4,
      thisMonthSpent: 850000,
      pendingQuotes: 23,
      urgentRFQs: 2,
      newMessages: 15,
      supplierConnections: 45,
      costSavings: 525000,
      avgSupplierRating: 4.6,
      profileViews: 89
    },
    recentActivity: [
      {
        id: 1,
        type: 'rfq_created',
        title: 'RFQ created for Office Equipment',
        category: 'Office Equipment',
        budget: 250000,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        status: 'active',
        responses: 8
      },
      {
        id: 2,
        type: 'quote_received',
        title: 'Quote received for Cleaning Services',
        supplier: 'CleanPro Services',
        amount: 180000,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        status: 'reviewing'
      },
      {
        id: 3,
        type: 'rfq_completed',
        title: 'RFQ completed for IT Equipment',
        supplier: 'TechSolutions NG',
        amount: 320000,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
        status: 'completed'
      }
    ],
    topSuppliers: [
      {
        id: 1,
        name: 'TechSolutions NG',
        category: 'IT Equipment',
        rating: 4.8,
        totalQuotes: 12,
        avgResponseTime: 2.1,
        totalSpent: 450000,
        location: 'Lagos'
      },
      {
        id: 2,
        name: 'CleanPro Services',
        category: 'Cleaning',
        rating: 4.6,
        totalQuotes: 8,
        avgResponseTime: 3.5,
        totalSpent: 280000,
        location: 'Abuja'
      },
      {
        id: 3,
        name: 'OfficeMax Supplies',
        category: 'Office Equipment',
        rating: 4.7,
        totalQuotes: 15,
        avgResponseTime: 1.8,
        totalSpent: 520000,
        location: 'Lagos'
      }
    ],
    performanceMetrics: {
      avgResponseTime: 4.2,
      avgQuotesPerRFQ: 12,
      avgSupplierRating: 4.6,
      costSavings: 15
    },
    marketInsights: {
      trendingCategories: [
        { name: 'IT Equipment', growth: '+25%', demand: 'High' },
        { name: 'Cleaning Services', growth: '+18%', demand: 'Medium' },
        { name: 'Office Supplies', growth: '+12%', demand: 'Medium' }
      ],
      topLocations: [
        { name: 'Lagos', suppliers: 156, avgRating: 4.5 },
        { name: 'Abuja', suppliers: 89, avgRating: 4.3 },
        { name: 'Port Harcourt', suppliers: 67, avgRating: 4.4 }
      ]
    }
  });

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'rfq_created': return <PlusIcon className="h-5 w-5 text-blue-600" />;
      case 'quote_received': return <DocumentTextIcon className="h-5 w-5 text-green-600" />;
      case 'rfq_completed': return <CheckCircleIcon className="h-5 w-5 text-purple-600" />;
      default: return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading your buyer dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-2">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-4">
                    <ShoppingBagIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Buyer Dashboard
                    </h1>
                    <p className="text-gray-600 mb-2">
                      Welcome back, {userProfile?.fullName || user?.displayName || 'Buyer'}! 
                      Here's your procurement overview.
                    </p>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {dashboardData.stats.completedRFQs} RFQs completed
                      </span>
                      <span className="flex items-center text-blue-600">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {dashboardData.stats.activeRFQs} active RFQs
                      </span>
                      <span className="flex items-center text-purple-600">
                        <ChatAlt2Icon className="h-4 w-4 mr-1" />
                        {dashboardData.stats.newMessages} new messages
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <button className="relative px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                  <BellIcon className="h-5 w-5 mr-2 inline" />
                  Notifications
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    5
                  </span>
                </button>
                <button 
                  onClick={() => navigate('/create-rfq')}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 font-semibold"
                >
                  Create RFQ
                </button>
                <button 
                  onClick={signOut}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300 font-semibold flex items-center"
                >
                  <LockClosedIcon className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total RFQs</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.totalRFQs}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{dashboardData.stats.thisMonthRFQs} this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Spent</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(dashboardData.stats.totalSpent)}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{formatCurrency(dashboardData.stats.thisMonthSpent)} this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.avgResponseTime}h</p>
                <div className="flex items-center mt-2">
                  <ArrowDownIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">-0.8h this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Quotes/RFQ</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.avgQuotesPerRFQ}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+2 this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <UserGroupIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Pending Quotes</p>
                <p className="text-2xl font-bold text-yellow-900">{dashboardData.stats.pendingQuotes}</p>
                <p className="text-xs text-yellow-700">Need review</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border border-red-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Urgent RFQs</p>
                <p className="text-2xl font-bold text-red-900">{dashboardData.stats.urgentRFQs}</p>
                <p className="text-xs text-red-700">Time sensitive</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <ExclamationIcon className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">New Messages</p>
                <p className="text-2xl font-bold text-blue-900">{dashboardData.stats.newMessages}</p>
                <p className="text-xs text-blue-700">Unread</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ChatAlt2Icon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Supplier Connections</p>
                <p className="text-2xl font-bold text-green-900">{dashboardData.stats.supplierConnections}</p>
                <p className="text-xs text-green-700">Active</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', name: 'Overview', icon: EyeIcon },
                { id: 'suppliers', name: 'Top Suppliers', icon: UserGroupIcon },
                { id: 'activity', name: 'Recent Activity', icon: ClockIcon },
                { id: 'insights', name: 'Market Insights', icon: SparklesIcon },
                { id: 'analytics', name: 'Procurement Analytics', icon: TrendingUpIcon },
                { id: 'performance', name: 'Performance', icon: ChartBarIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Success Banner */}
                <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-2xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2">🎯 Procurement Success!</h3>
                      <p className="text-blue-100 mb-3">
                        You've saved {dashboardData.performanceMetrics.costSavings}% on average through competitive bidding. 
                        Your procurement strategy is working excellently!
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                          {formatCurrency(dashboardData.stats.thisMonthSpent)} spent this month
                        </span>
                        <span className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {dashboardData.stats.avgQuotesPerRFQ} avg quotes per RFQ
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{dashboardData.performanceMetrics.costSavings}%</div>
                      <div className="text-blue-100 text-sm">Cost Savings</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Quick Actions */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <SparklesIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => navigate('/create-rfq')}
                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <PlusIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Create New RFQ</p>
                            <p className="text-sm text-gray-600">Post your requirements</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <button
                        onClick={() => navigate('/my-rfqs')}
                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <DocumentTextIcon className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">View My RFQs</p>
                            <p className="text-sm text-gray-600">Manage your requests</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <button
                        onClick={() => navigate('/my-responses')}
                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                            <UserGroupIcon className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Review Quotes</p>
                            <p className="text-sm text-gray-600">Evaluate supplier offers</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-green-900">💡 Pro Tip</h4>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">New</span>
                        </div>
                        <p className="text-sm text-green-800 mb-3">
                          Buyers who provide detailed RFQs receive 40% more competitive quotes. Be specific about your requirements!
                        </p>
                        <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium">
                          Learn Best Practices
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Performance Summary */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <ChartBarIcon className="h-5 w-5 mr-2 text-purple-600" />
                      Performance Summary
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Avg Response Time</span>
                        <span className="font-semibold text-gray-900">{dashboardData.performanceMetrics.avgResponseTime}h</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Avg Quotes per RFQ</span>
                        <span className="font-semibold text-gray-900">{dashboardData.performanceMetrics.avgQuotesPerRFQ}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Avg Supplier Rating</span>
                        <span className="font-semibold text-gray-900">{dashboardData.performanceMetrics.avgSupplierRating}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-purple-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Cost Savings</span>
                        <div className="flex items-center">
                          <span className="font-semibold text-gray-900 mr-2">{dashboardData.performanceMetrics.costSavings}%</span>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <StarIcon 
                                key={star} 
                                className={`h-4 w-4 ${
                                  star <= Math.floor(dashboardData.performanceMetrics.costSavings / 3)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`} 
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Suppliers Tab */}
            {activeTab === 'suppliers' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Top Performing Suppliers</h3>
                  <button className="flex items-center text-sm text-blue-600 hover:text-blue-700">
                    <RefreshIcon className="h-4 w-4 mr-1" />
                    Refresh
                  </button>
                </div>
                
                <div className="space-y-4">
                  {dashboardData.topSuppliers.map((supplier) => (
                    <div key={supplier.id} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-gray-900">{supplier.name}</h4>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {supplier.category}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                            <span className="flex items-center">
                              <LocationMarkerIcon className="h-4 w-4 mr-1" />
                              {supplier.location}
                            </span>
                            <span className="flex items-center">
                              <StarIcon className="h-4 w-4 mr-1" />
                              {supplier.rating} rating
                            </span>
                            <span className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              {supplier.totalQuotes} quotes
                            </span>
                            <span className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {supplier.avgResponseTime}h avg response
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-500">
                            <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                            <span>{formatCurrency(supplier.totalSpent)} total spent</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm">
                            View Profile
                          </button>
                          <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 text-sm">
                            Contact
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                  <button className="text-sm text-blue-600 hover:text-blue-700">View All</button>
                </div>
                
                <div className="space-y-4">
                  {dashboardData.recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        {getActivityIcon(activity.type)}
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{activity.title}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          {activity.supplier && <span>{activity.supplier}</span>}
                          {activity.category && <span>• {activity.category}</span>}
                          <span>• {formatCurrency(activity.amount || activity.budget)}</span>
                          <span>• {formatTime(activity.timestamp)}</span>
                          {activity.responses && <span>• {activity.responses} responses</span>}
                        </div>
                      </div>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                        {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Market Insights Tab */}
            {activeTab === 'insights' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Market Insights & Trends</h3>
                  <span className="text-sm text-gray-500">Real-time market data</span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Trending Categories */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUpIcon className="h-5 w-5 mr-2 text-purple-600" />
                      Trending Categories
                    </h4>
                    <div className="space-y-3">
                      {dashboardData.marketInsights.trendingCategories.map((category, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">{category.name}</span>
                            <span className={`text-sm font-semibold ${
                              category.growth.startsWith('+') ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {category.growth}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">Demand: {category.demand}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Locations */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <GlobeAltIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Top Supplier Locations
                    </h4>
                    <div className="space-y-3">
                      {dashboardData.marketInsights.topLocations.map((location, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">{location.name}</span>
                            <span className="text-sm text-blue-600 font-semibold">{location.suppliers} suppliers</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Avg Rating</span>
                            <div className="flex items-center">
                              <span className="text-sm font-semibold text-gray-900 mr-1">{location.avgRating}</span>
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIcon 
                                    key={star} 
                                    className={`h-3 w-3 ${
                                      star <= Math.floor(location.avgRating)
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-gray-300'
                                    }`} 
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Procurement Tips */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <LightningBoltIcon className="h-5 w-5 mr-2 text-green-600" />
                    Procurement Best Practices
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Detailed RFQs</p>
                      <p className="text-xs text-gray-600">Be specific about requirements</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <ClockIcon className="h-6 w-6 text-green-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Quick Decisions</p>
                      <p className="text-xs text-gray-600">Respond within 48 hours</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <StarIcon className="h-6 w-6 text-purple-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Rate Suppliers</p>
                      <p className="text-xs text-gray-600">Help improve quality</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Procurement Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Procurement Analytics & Insights</h3>
                  <span className="text-sm text-gray-500">Data-driven procurement decisions</span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Cost Analysis */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 mr-2 text-green-600" />
                      Cost Analysis
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Total Spent</span>
                          <span className="text-sm text-green-600 font-semibold">{formatCurrency(dashboardData.stats.totalSpent)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Cost Savings</span>
                          <span className="text-sm text-green-600 font-semibold">{formatCurrency(dashboardData.stats.costSavings)}</span>
                        </div>
                        <p className="text-xs text-gray-600">15% savings through competitive bidding</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Avg Quote Value</span>
                          <span className="text-sm text-blue-600 font-semibold">{formatCurrency(dashboardData.stats.totalSpent / dashboardData.stats.totalRFQs)}</span>
                        </div>
                        <p className="text-xs text-gray-600">Per RFQ average</p>
                      </div>
                    </div>
                  </div>

                  {/* Supplier Performance */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Supplier Performance
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Avg Supplier Rating</span>
                          <div className="flex items-center">
                            <span className="text-sm font-semibold text-gray-900 mr-2">{dashboardData.stats.avgSupplierRating}</span>
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon 
                                  key={star} 
                                  className={`h-4 w-4 ${
                                    star <= Math.floor(dashboardData.stats.avgSupplierRating)
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-gray-300'
                                  }`} 
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Active Suppliers</span>
                          <span className="text-sm text-blue-600 font-semibold">{dashboardData.stats.supplierConnections}</span>
                        </div>
                        <p className="text-xs text-gray-600">Connected suppliers</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Avg Response Time</span>
                          <span className="text-sm text-purple-600 font-semibold">{dashboardData.stats.avgResponseTime}h</span>
                        </div>
                        <p className="text-xs text-gray-600">From suppliers</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Procurement Efficiency */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <TrendingUpIcon className="h-5 w-5 mr-2 text-purple-600" />
                    Procurement Efficiency Metrics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <DocumentTextIcon className="h-8 w-8 text-green-600" />
                      </div>
                      <p className="text-lg font-bold text-gray-900">{dashboardData.stats.avgQuotesPerRFQ}</p>
                      <p className="text-sm text-gray-600">Avg Quotes/RFQ</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <ClockIcon className="h-8 w-8 text-blue-600" />
                      </div>
                      <p className="text-lg font-bold text-gray-900">{dashboardData.stats.avgResponseTime}h</p>
                      <p className="text-sm text-gray-600">Avg Response Time</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                      </div>
                      <p className="text-lg font-bold text-gray-900">{dashboardData.stats.completedRFQs}</p>
                      <p className="text-sm text-gray-600">Completed RFQs</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
                      </div>
                      <p className="text-lg font-bold text-gray-900">15%</p>
                      <p className="text-sm text-gray-600">Cost Savings</p>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <SparklesIcon className="h-5 w-5 mr-2 text-yellow-600" />
                    Smart Recommendations
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                          <span className="text-xs font-bold text-blue-600">1</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Optimize RFQ descriptions</p>
                          <p className="text-xs text-gray-600">Detailed RFQs receive 40% more competitive quotes</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                          <span className="text-xs font-bold text-green-600">2</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Expand supplier network</p>
                          <p className="text-xs text-gray-600">Consider suppliers from Abuja for better pricing</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                          <span className="text-xs font-bold text-purple-600">3</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Quick decision making</p>
                          <p className="text-xs text-gray-600">Respond to quotes within 48 hours for better supplier relationships</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-1">
                          <span className="text-xs font-bold text-orange-600">4</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Rate suppliers regularly</p>
                          <p className="text-xs text-gray-600">Help improve overall supplier quality and service</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Performance Analytics</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">This Month</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">RFQs Created</span>
                        <span className="font-semibold">{dashboardData.stats.thisMonthRFQs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Amount Spent</span>
                        <span className="font-semibold">{formatCurrency(dashboardData.stats.thisMonthSpent)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Avg Response Time</span>
                        <span className="font-semibold">{dashboardData.stats.avgResponseTime}h</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Goals & Targets</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Monthly RFQs Target</span>
                        <span className="font-semibold">4/6</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '67%' }}></div>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Cost Savings Target</span>
                        <span className="font-semibold">15%/20%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Response Time Target</span>
                        <span className="font-semibold">4.2h/3h</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{ width: '70%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerDashboard; 